import * as React from 'react'
import { Slot } from '@radix-ui/react-slot'
import { cva, type VariantProps } from 'class-variance-authority'
import { cn } from '~/lib/common-utils'

const buttonVariants = cva(
  'flex gap-2 font-lexend items-center justify-center whitespace-nowrap rounded-sm text-sm font-medium ring-offset-white transition-all duration-200 ease-in-out focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-neutral-950 focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50',
  {
    variants: {
      variant: {
        default: 'bg-white text-primary-500 hover:bg-primary-500 hover:text-white hover:shadow-md',
        primary: 'bg-primary-500 text-white hover:bg-[#75523A] hover:shadow-md',
        outline:
          'border border-primary-500 text-primary-500 hover:bg-primary-500 hover:text-white hover:shadow-sm',
        secondary: 'bg-neutral-100 text-neutral-900 hover:bg-neutral-200 hover:shadow',
        ghost: 'text-primary-500 hover:bg-neutral-100 hover:text-primary-500 hover:underline',
        link: 'text-primary-500 underline-offset-4 hover:underline hover:text-[#5e3f2e]',
      },
      size: {
        default: 'h-14 rounded-[4px] p-3 md:p-4',
        sm: 'h-9 rounded-md px-3',
        lg: 'h-11 rounded-md px-8',
        xl: 'h-12 rounded-md px-10 text-base',
        icon: 'h-10 w-10',
      },
    },
    defaultVariants: {
      variant: 'default',
      size: 'default',
    },
  }
)

export interface ButtonProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement>,
    VariantProps<typeof buttonVariants> {
  asChild?: boolean
}

const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  ({ className, variant, size, asChild = false, ...props }, ref) => {
    const Comp = asChild ? Slot : 'button'
    return (
      <Comp className={cn(buttonVariants({ variant, size, className }))} ref={ref} {...props} />
    )
  }
)
Button.displayName = 'Button'

export { Button, buttonVariants }
